'use client';

import Image from 'next/image';
import { useLayoutEffect, useRef } from 'react';
import type { ComponentPropsWithoutRef, CSSProperties } from 'react';
import { cx } from '../../utils/cx';
import styles from './AnchoredImage.module.css';

export type ImageAnchor = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type AnchoredImageProps = Omit<
  ComponentPropsWithoutRef<'div'>,
  'children'
> & {
  src: string;
  sizes: string;
  alt: string;
  sourceWidth: number;
  sourceHeight: number;
  anchor?: ImageAnchor;
  imageClassName?: string;
  priority?: boolean;
  pixelSnap?: boolean;
};

export const AnchoredImage = ({
  src,
  sizes,
  alt,
  sourceWidth,
  sourceHeight,
  anchor,
  className,
  imageClassName,
  priority = false,
  pixelSnap = false,
  style,
  ...props
}: AnchoredImageProps) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const region = anchor ?? {
    x: 0,
    y: 0,
    width: sourceWidth,
    height: sourceHeight,
  };
  const frameStyle: CSSProperties = {
    aspectRatio: `${region.width} / ${region.height}`,
    ...style,
  };
  const imageStyle: CSSProperties = {
    width: `${(sourceWidth / region.width) * 100}%`,
    height: 'auto',
    left: `${(-region.x / region.width) * 100}%`,
    top: `${(-region.y / region.height) * 100}%`,
  };

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame || !pixelSnap) return;
    const snap = () => {
      frame.style.translate = 'none';
      const rect = frame.getBoundingClientRect();
      frame.style.translate = `${Math.round(rect.x) - rect.x}px ${Math.round(rect.y) - rect.y}px`;
    };
    const observer = new ResizeObserver(snap);
    observer.observe(frame);
    window.addEventListener('resize', snap);
    snap();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', snap);
      frame.style.translate = '';
    };
  }, [pixelSnap]);

  return (
    <div
      className={cx(styles.frame, className)}
      ref={frameRef}
      style={frameStyle}
      {...props}
    >
      <Image
        className={cx(styles.image, imageClassName)}
        src={src}
        sizes={sizes}
        quality={100}
        alt={alt}
        width={sourceWidth}
        height={sourceHeight}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding={priority ? 'sync' : 'async'}
        style={imageStyle}
      />
    </div>
  );
};
